import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  AlertCircle,
  CloudUpload,
  ShieldCheck,
  Sparkles,
  Cpu,
  History,
} from 'lucide-react';
import { logger } from '@/utils/logger';
import {
  cloudSourceService,
  CloudSourceListRow,
  CloudProviderAvailability,
  notifyCloudSourcesChanged,
} from '@/services/cloudSourceService';
import CloudSourceConnectButtons from '@/components/knowledge/CloudSourceConnectButtons';
import CloudSourceCard from '@/components/knowledge/CloudSourceCard';

/**
 * Connectors (Import from Cloud) - standalone page (split out of KBUploadPage).
 *
 * Connect Google Drive / SharePoint and manage connected sources. The
 * device-upload flow lives at /dashboard/admin/upload; the two remain
 * mutually exclusive (a connected cloud account blocks device upload -
 * that rule is enforced on the upload page).
 */
const KBCloudImportPage: React.FC = () => {
  const [cloudAvailability, setCloudAvailability] = useState<CloudProviderAvailability | null>(null);
  const [cloudSources, setCloudSources] = useState<CloudSourceListRow[]>([]);
  const [cloudLoading, setCloudLoading] = useState(true);

  const reloadCloudSources = useCallback(async () => {
    try {
      const res = await cloudSourceService.listMySources();
      setCloudSources(res.sources || []);
      // Tell the sidebar to re-check whether a connector is active, so the
      // manual "Upload Documents" nav entry hides/shows immediately after a
      // connect/disconnect/delete instead of only on the next page reload.
      notifyCloudSourcesChanged();
    } catch (err: unknown) {
      logger.warn('Failed to load cloud sources', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setCloudLoading(true);
        const [avail] = await Promise.all([
          cloudSourceService.getProviderAvailability(),
          reloadCloudSources(),
        ]);
        if (!cancelled) setCloudAvailability(avail);
      } catch (err: unknown) {
        logger.warn('Failed to load cloud provider availability', err);
      } finally {
        if (!cancelled) setCloudLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadCloudSources]);

  // A provider already has a usable card - so we must NOT also offer an
  // "Add a connection" card for it - once it is either Connected, or was
  // authorized at some point and can be reconnected from its own card
  // (Disconnected / Expired / Error / Failed all show a Reconnect button).
  //
  // `connected_account_email` is the marker for "authorized at least once":
  // it is only ever set after a successful OAuth and is never cleared. Using
  // it keeps the original guard intact - a never-authorized placeholder row
  // (popup closed mid-OAuth) has no email, so the authorize card stays.
  const isSetUp = (s: CloudSourceListRow) =>
    s.connection_status === 'Connected' || !!s.connected_account_email;
  const hasDriveSource = cloudSources.some(
    (s) => s.provider === 'Google Drive' && isSetUp(s),
  );
  const hasSharePointSource = cloudSources.some(
    (s) => s.provider === 'SharePoint' && isSetUp(s),
  );
  const connectedCount = cloudSources.filter(
    (s) => s.connection_status === 'Connected',
  ).length;
  const cloudSyncEnabled = cloudAvailability?.cloud_sync_enabled !== false;

  return (
    <div className="dark bg-background text-foreground min-h-full space-y-6 max-w-full px-4 sm:px-8 py-4 sm:py-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-border bg-primary/10">
            <CloudUpload className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Connectors</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage connected enterprise repositories for ingestion and knowledge indexing. Files are processed in memory, and original file data is never stored.
            </p>
          </div>
        </div>
        {!cloudLoading && cloudSyncEnabled && (
          <span
            className={
              'inline-flex flex-shrink-0 items-center gap-2 self-start rounded-full border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] ' +
              (connectedCount > 0
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                : 'border-rose-500/30 bg-rose-500/10 text-rose-300')
            }
          >
            <span
              className={
                'inline-block h-1.5 w-1.5 rounded-full ' +
                (connectedCount > 0 ? 'bg-emerald-400' : 'bg-rose-400')
              }
            />
            {connectedCount > 0
              ? `${connectedCount} Connection${connectedCount === 1 ? '' : 's'} Active`
              : 'Awaiting Authorization'}
          </span>
        )}
      </div>

      {cloudLoading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading cloud sources…
        </div>
      ) : !cloudSyncEnabled ? (
        // Master switch is off - hide the Connect controls and any
        // existing sources entirely; surface the admin-facing
        // explanation instead.
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-muted/20 px-6 py-16 text-center">
          <CloudUpload className="h-8 w-8 text-muted-foreground" />
          <h2 className="text-base font-semibold">Cloud sync is not enabled</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {cloudAvailability?.message ||
              "Ask your administrator to turn on 'Cloud Sync Enabled' in KB Cloud Sync Settings to connect Google Drive or SharePoint."}
          </p>
        </div>
      ) : (
        <>
          {cloudAvailability?.message && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{cloudAvailability.message}</span>
            </div>
          )}

          {/* Connected sources and the authorize cards for providers not yet
              connected share ONE grid, so an "Add a connection" card sits on
              the same row as a connected source rather than below it.
              `layout="contents"` dissolves the connect-buttons wrapper so its
              cards become items of this grid. */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            {cloudSources.map((src) => (
              <CloudSourceCard
                key={src.name}
                source={src}
                onRefresh={reloadCloudSources}
                dailySyncTime={cloudAvailability?.daily_sync_time}
              />
            ))}
            {(!hasDriveSource || !hasSharePointSource) && (
              <CloudSourceConnectButtons
                driveAvailable={cloudAvailability?.drive !== false}
                sharepointAvailable={cloudAvailability?.sharepoint !== false}
                hasDriveSource={hasDriveSource}
                hasSharePointSource={hasSharePointSource}
                onConnected={reloadCloudSources}
                layout="contents"
              />
            )}
          </div>

          {/* Security strip - honest claims only */}
          <div className="flex flex-col gap-4 rounded-xl border border-border bg-card/60 px-5 py-4 sm:flex-row sm:items-center">
            <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-border bg-muted/40">
              <ShieldCheck className="h-5 w-5 text-primary" />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em]">
                Enterprise Security Protocols
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                All connections are authorized through OAuth 2.0, with access
                tokens held server-side and never exposed to the browser.
                Documents are processed in memory during import - extracted
                intelligence is retained, original files are never stored.
              </p>
            </div>
          </div>

          {/* Capability tiles */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-card/60 p-4">
              <Sparkles className="h-5 w-5 text-primary" />
              <p className="mt-3 text-sm font-semibold">Intelligent Indexing</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Automated entity recognition, relationship mapping, and
                classification on every imported document.
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card/60 p-4">
              <Cpu className="h-5 w-5 text-primary" />
              <p className="mt-3 text-sm font-semibold">In-Memory Processing</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Documents are streamed and processed entirely in memory -
                extracted knowledge is retained, source files are not.
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card/60 p-4">
              <History className="h-5 w-5 text-primary" />
              <p className="mt-3 text-sm font-semibold">Sync Activity Log</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Every sync run and per-file outcome is recorded for full
                auditability - open View Activity on any source to trace it.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default KBCloudImportPage;
